<template>
  <span class="ds-status-tag" :class="['ds-status-tag--' + resolved.type, 'ds-status-tag--' + size]">
    <i class="ds-status-tag__dot" v-if="dot"></i>
    <span class="ds-status-tag__text"><slot>{{ resolved.label }}</slot></span>
  </span>
</template>

<script>
import { resolveStatus } from '@/utils/statusMap'

/**
 * DsStatusTag —— 语义状态标签
 *
 * 铁律（WCAG 1.4.1）：状态**必须**用文字表达，颜色仅作辅助，
 * 因此本组件始终渲染文字，不提供「纯色块」模式。
 *
 * Props:
 *  - type    {String} success|warning|danger|info|primary|muted
 *  - label   {String} 展示文案
 *  - dot     {Boolean} 是否显示圆点
 *  - size    {String} sm|default
 *  - value   {String|Number} 后端原始状态值，配合 domain 使用
 *  - domain  {String} 业务域，见 src/utils/statusMap.js
 *  - text    {String} 后端状态文案，用于语义推断
 */
export default {
  name: 'DsStatusTag',
  props: {
    type: {
      type: String,
      default: 'muted'
    },
    label: {
      type: String,
      default: ''
    },
    dot: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'default'
    },
    value: {
      type: [String, Number],
      default: ''
    },
    domain: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    }
  },
  computed: {
    resolved () {
      if (this.type && this.label) {
        return { type: this.type, label: this.label }
      }
      return resolveStatus(this.value, {
        domain: this.domain,
        text: this.text,
        fallbackLabel: this.label
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-status-tag {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-spacing-xs);
  padding: 2px 10px;
  border-radius: var(--ds-radius-full);
  font-size: var(--ds-font-size-xs);
  font-weight: 500;
  line-height: 1.6;
  white-space: nowrap;

  &.ds-status-tag--sm {
    padding: 0 8px;
    font-size: 11px;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }

  &--success {
    background-color: rgba(var(--ds-success-rgb), 0.1);
    color: var(--ds-success);
  }

  &--warning {
    background-color: rgba(var(--ds-warning-rgb), 0.12);
    color: #B45309;
  }

  &--danger {
    background-color: rgba(var(--ds-danger-rgb), 0.1);
    color: var(--ds-danger);
  }

  &--info {
    background-color: rgba(var(--ds-info-rgb), 0.1);
    color: var(--ds-info);
  }

  &--primary {
    background-color: rgba(var(--ds-primary-rgb), 0.1);
    color: var(--ds-primary);
  }

  &--muted {
    background-color: var(--ds-gray-200);
    color: var(--ds-gray-600);
  }
}
</style>
